import { useCallback, useId, useRef, useState } from "react";
import dayjs from "dayjs";
import { FieldProps, FormHelperText } from "../FieldBase";
import { FormControl } from "../FormControl";
import { InputBase, InputBaseProps } from "../InputBase";
import { InputLabel } from "../InputLabel";
import { VisuallyHidden } from "../VisuallyHidden/VisuallyHidden";
import { useDitectInputTouched } from "../FieldBase/useDitectInputTouched";
import { useFieldGroup } from "../FieldGroup";
import { useInputValue } from "../hooks/useInputValue";
import { useMergeRefs } from "../hooks/useMergeRefs";
import { useValidation } from "../FieldBase/useValidation";
import { getDayjs } from "./getDayjs";

export interface DateTextFieldProps
  extends
    FieldProps<dayjs.Dayjs | undefined>,
    Omit<InputBaseProps, "value" | "onChange" | "defaultValue"> {
  type?: "never";
  /**
   * 日付のフォーマットを指定します。
   * @default "YYYY-MM-DD"
   */
  format?: string;
  /**
   * デフォルトでは、formatがplaceholderとして表示されます。
   */
  placeholder?: string;

  /**
   * 最小の日付を指定します。
   * minDate未満の場合、バリデーションエラーが発生します。
   */
  minDate?: dayjs.Dayjs;
  /**
   * 最大の日付を指定します。
   * maxDateより大きい場合、バリデーションエラーが発生します。
   */
  maxDate?: dayjs.Dayjs;
}

export const DateTextField = (props: DateTextFieldProps) => {
  const {
    label,
    style,
    className,
    onChange,
    onFocus,
    onBlur,
    defaultValue,
    value: _propsValue,
    disabled,
    validationMessages,
    customValidations,
    helperText,
    invalid,
    loading: propsLoading,
    readOnly: propsReadonly,
    id,
    inputRef,
    format = "YYYY-MM-DD",
    required,
    name,
    minDate,
    maxDate,
    ...restProps
  } = props;
  const ref = useRef<HTMLInputElement>(null);
  const refs = useMergeRefs(inputRef, ref);

  const [value, setValue, isControlled] = useInputValue(props);
  const [query, setQuery] = useState<string>(value?.format(format) ?? "");
  const { loading, readOnly } = useFieldGroup({
    loading: propsLoading,
    readOnly: propsReadonly,
    onLoaded: () => {
      if (isControlled === false) {
        setValue(defaultValue);
      }
    },
  });

  const validation = useValidation({
    ref,
    validationMessages,
    customValidations: [
      ...(customValidations ?? []),
      (value) => {
        if (!value) {
          return "";
        }
        if (minDate && value.isBefore(minDate, "day")) {
          return `${minDate.format(format)}以降の日付を入力してください。`;
        }
        if (maxDate && value.isAfter(maxDate, "day")) {
          return `${maxDate.format(format)}以前の日付を入力してください。`;
        }
        return "";
      },
    ],
    value,
  });
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.currentTarget.value;
      setQuery(value);
      const date = getDayjs(value);
      setValue(date);
      if (onChange) {
        onChange(date, event);
      }
    },
    [onChange, setValue],
  );

  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      if (onBlur) {
        onBlur(event);
      }
      setQuery(value?.format(format) ?? "");
    },
    [format, onBlur, value],
  );

  const [touched, inputProps] = useDitectInputTouched(onFocus);

  const internalInputId = useId();
  const inputId = id ?? internalInputId;

  const formattedValue = value?.format(format) ?? "";

  return (
    <FormControl style={style} className={className}>
      <InputLabel
        htmlFor={inputId}
        aria-disabled={disabled}
        required={required}
        loading={loading}
      >
        {label}
      </InputLabel>
      <VisuallyHidden>
        <input
          type="text"
          required={required}
          name={name}
          /**
           * value + readOnlyなどの場合、requiredなどのチェックが行われなくなるため
           * defaultValue + keyで代用している。
           */
          defaultValue={formattedValue}
          key={formattedValue}
          autoCapitalize="none"
          autoComplete="off"
          autoCorrect="off"
          spellCheck="false"
          tabIndex={-1}
        />
      </VisuallyHidden>
      <InputBase
        id={inputId}
        disabled={disabled}
        loading={loading}
        readOnly={readOnly}
        type="text"
        value={query}
        onChange={handleChange}
        onKeyDown={(event) => {
          /**
           * Enterキーが押された場合、クエリをフォーマット済みの値に変換してセットする。
           */
          if (event.key === "Enter") {
            setQuery(formattedValue);
          }
        }}
        data-invalid={!validation.valid}
        ref={refs}
        onBlur={handleBlur}
        inputMode="numeric"
        placeholder={format}
        {...restProps}
        {...inputProps}
      />
      <FormHelperText
        invalid={!validation.valid || invalid}
        loading={loading}
        touched={touched}
        value={value}
      >
        {
          // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
          validation.message || helperText
        }
      </FormHelperText>
    </FormControl>
  );
};
